import { useEffect, useState } from "react";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { ExistingProject } from "../models/project";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";
import BaseInputText from "./BaseInputText";
import BaseInputTextArea from "./BaseInputTextArea";
import BaseModal from "./BaseModal";

interface EditProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExistingProject;
  onSave: (projectData: {
    title: string;
    description: string;
  }) => Promise<void>;
  isUpdating: boolean;
}

export default function EditProjectDialog({
  isOpen,
  onClose,
  project,
  onSave,
  isUpdating,
}: EditProjectDialogProps) {
  const [title, setTitle] = useState(project.title);
  const [description, setDescription] = useState(project.description || "");

  // Update local state when project changes
  useEffect(() => {
    setTitle(project.title);
    setDescription(project.description || "");
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({ title, description });
  };

  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl font-semibold">
        Edit Project
      </h1>
      <button type="button" onClick={onClose}>
        <XMarkIcon
          aria-label="close edit project dialog"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      closeModal={onClose}
      open={isOpen}
      title={header}
      allowOverflowY={true}
      parentDivStyles="inline-block transform overflow-hidden max-w-md rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="px-6 pb-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <BaseInputText
            admin={true}
            inputName="project-title"
            text="Project Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter project title"
            required
          />

          <BaseInputTextArea
            admin={true}
            inputName="project-description"
            text="Description"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
            placeholder="Enter project description (optional)"
            rows={4}
          />

          <div className="mt-6 flex justify-end space-x-3">
            <BaseButtonSecondary
              type="button"
              onClick={onClose}
              disabled={isUpdating}
            >
              Cancel
            </BaseButtonSecondary>
            <BaseButtonPrimary
              type="submit"
              disabled={isUpdating}
              isBusy={isUpdating}
              busyText="Saving..."
            >
              Save Changes
            </BaseButtonPrimary>
          </div>
        </form>
      </div>
    </BaseModal>
  );
}
